import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { User, Mail, Lock, CheckCircle2 } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

export function SettingsProfilePage() {
  const profile = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => (await api.get('/api/auth/me')).data,
  });

  // --- Password change ---
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submitPasswordChange(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords don\'t match');
      return;
    }
    if (newPassword.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
      toast.success('Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error('Password change failed', extractApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  }

  const user = profile.data?.user;

  if (profile.isLoading || !user) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Profile</h1>
        <p className="text-sm text-muted-foreground">
          Personal information, email, and password.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your account</CardTitle>
          <CardDescription>Identity details on this tenant.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow icon={User} label="Name" value={`${user.firstName} ${user.lastName}`} />
          <InfoRow
            icon={Mail}
            label="Email"
            value={
              <span className="flex items-center gap-2">
                {user.email}
                {user.emailVerifiedAt ? (
                  <Badge variant="success"><CheckCircle2 className="mr-1 h-3 w-3" />Verified</Badge>
                ) : (
                  <Badge variant="warning">Unverified</Badge>
                )}
              </span>
            }
          />
          <InfoRow label="Role" value={<Badge variant="secondary">{user.role}</Badge>} />
          <InfoRow label="21 CFR Part 11 e-signature" value={user.signatureAgreed ? <Badge variant="success">Agreed</Badge> : <Badge variant="warning">Not yet accepted</Badge>} />
          <InfoRow label="Two-factor auth" value={user.mfaEnabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="secondary">Not enabled</Badge>} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Change password</CardTitle>
          <CardDescription>
            Updating your password will revoke all other active sessions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitPasswordChange} className="grid max-w-md gap-3">
            <div>
              <Label htmlFor="cpw">Current password</Label>
              <Input
                id="cpw"
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="npw">New password</Label>
              <Input
                id="npw"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">Minimum 12 characters.</p>
            </div>
            <div>
              <Label htmlFor="npwc">Confirm new password</Label>
              <Input
                id="npwc"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <div>
              <Button type="submit" disabled={submitting}>
                <Lock className="mr-1.5 h-3.5 w-3.5" />
                {submitting ? 'Updating...' : 'Update password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[160px,1fr] items-center gap-4 border-b pb-3 last:border-0 last:pb-0">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
